import {
  Button,
  Card,
  CardHeader,
  FormField,
  MultiSelectTags,
} from "@constructorfleet/ui/design-system";
import { useEffect, useMemo, useState } from "react";
import type { BhgAdminUser, ProductDbClient } from "@constructorfleet/bhg-product-core";

export function TriggerCallCard({ dbClient }: { dbClient: ProductDbClient }) {
  const [manualCallUsers, setManualCallUsers] = useState<BhgAdminUser[]>([]);
  const [selectedManualCallUsernames, setSelectedManualCallUsernames] = useState<string[]>([]);
  const [manualCallMessage, setManualCallMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    if (!dbClient.listUsers)
      return () => {
        mounted = false;
      };
    void (async () => {
      const users = await dbClient.listUsers?.();
      if (!mounted || !users) return;
      setManualCallUsers(users);
    })();
    return () => {
      mounted = false;
    };
  }, [dbClient]);

  const selectableManualUsers = useMemo(
    () =>
      manualCallUsers.filter((user) => {
        const username = (user.username ?? "").trim();
        // Eligibility is gated on an active entitlement, not role — an admin
        // who holds the BHG entitlement can receive intervention calls too.
        const hasEntitlement = user.billingStatus === "active" || user.billingStatus === "trialing";
        return user.isActive && hasEntitlement && username.length > 0;
      }),
    [manualCallUsers]
  );
  const eligibleUserCount = selectableManualUsers.length;

  return (
    <Card>
      <CardHeader title="Manual Intervention" meta={`Eligible users: ${eligibleUserCount}`} />
      <form
        className="card-body ds-stack"
        onSubmit={(event) => {
          event.preventDefault();
          setManualCallMessage(null);
          void (async () => {
            const selectedUsername = selectedManualCallUsernames[0];
            if (!selectedUsername) {
              setManualCallMessage("Select a user before triggering a call.");
              return;
            }
            const selectedUser = selectableManualUsers.find(
              (user) => user.username === selectedUsername
            );
            if (!selectedUser) {
              setManualCallMessage("Selected user is no longer available.");
              return;
            }
            if (!dbClient.requestAdmin) {
              setManualCallMessage(`Queued call for ${selectedUser.username}.`);
              return;
            }
            const result = await dbClient.requestAdmin<{
              queuedCount: number;
              initiatedCount?: number;
            }>({
              method: "POST",
              path: "/api/admin/bhg-call/trigger-calls",
              body: { userIds: [selectedUser.id] },
            });
            if (!result.ok) {
              setManualCallMessage(result.error);
              return;
            }
            setManualCallMessage(
              (result.data.initiatedCount ?? 0) > 0
                ? `Triggered call for ${selectedUser.username}.`
                : `Queued call for ${selectedUser.username}.`
            );
          })();
        }}
      >
        <FormField label="Choose one active user" htmlFor="bhg-manual-call-user">
          <MultiSelectTags
            id="bhg-manual-call-user"
            values={selectedManualCallUsernames}
            options={selectableManualUsers.map((user) => user.username)}
            onChange={(values) =>
              setSelectedManualCallUsernames(values.length > 0 ? [values[values.length - 1]] : [])
            }
            emptyLabel={
              eligibleUserCount > 0
                ? "No user selected."
                : "No active users are currently eligible."
            }
          />
        </FormField>
        <Button type="submit" disabled={selectedManualCallUsernames.length === 0}>
          Start intervention call
        </Button>
        {manualCallMessage ? (
          <p style={{ color: "var(--cf-text-muted)", fontSize: "13px", margin: 0 }}>
            {manualCallMessage}
          </p>
        ) : null}
      </form>
    </Card>
  );
}
